import { Button, Empty, Tag } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { DownloadOutlined, HistoryOutlined } from '@ant-design/icons'
import { DataTable } from '@kolab/dms-base/components/ui'
import { useInfiniteScroll } from '@kolab/dms-base/hooks'
import * as pingsApi from '../../../api/pings.api'
import type { Ping } from '../../../types/ping.types'
import { formatDate } from '../../../utils/formatters'
import { exportToCsv } from '../../../utils/csvExport'
import { QUERY_KEYS } from '../../../utils/constants'

interface ActivityLogViewProps {
  deviceId: string
}

function formatGeolocation(geo: { lat: number; lon: number } | null): string {
  if (!geo) return '--'
  return `${geo.lat.toFixed(4)}, ${geo.lon.toFixed(4)}`
}

function getStatusColor(status: string): string {
  switch (status) {
    case 'online': return 'green'
    case 'offline': return 'red'
    default: return 'default'
  }
}

function getStatusLabel(status: string): string {
  if (!status) return 'Unknown'
  return status.charAt(0).toUpperCase() + status.slice(1)
}

const columns: ColumnsType<Ping> = [
  {
    title: 'Date',
    dataIndex: 'pingAt',
    key: 'date',
    width: 200,
    sorter: (a, b) => new Date(a.pingAt).getTime() - new Date(b.pingAt).getTime(),
    defaultSortOrder: 'descend',
    render: (value: string) => (
      <span style={{ fontSize: 13, color: '#475569' }}>
        {formatDate(value)}
      </span>
    ),
  },
  {
    title: 'Status',
    key: 'status',
    width: 120,
    filters: [
      { text: 'Online', value: 'online' },
      { text: 'Offline', value: 'offline' },
      { text: 'Unknown', value: '' },
    ],
    onFilter: (value, record) => record.status === value,
    render: (_, record) => (
      <Tag color={getStatusColor(record.status)} style={{ borderRadius: 6, fontSize: 12 }}>
        {getStatusLabel(record.status)}
      </Tag>
    ),
  },
  {
    title: 'IP Address',
    key: 'ipAddress',
    width: 180,
    render: (_, record) => (
      <span style={{ fontSize: 13, color: '#475569', fontFamily: 'monospace' }}>
        {record.ipAddress || '--'}
      </span>
    ),
  },
  {
    title: 'Geolocation',
    key: 'geolocation',
    width: 200,
    render: (_, record) => (
      <span style={{ fontSize: 13, color: '#475569' }}>
        {formatGeolocation(record.geolocation)}
      </span>
    ),
  },
]

export default function ActivityLogView({ deviceId }: ActivityLogViewProps) {
  const { items, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useInfiniteScroll<Ping>({
      queryKey: [...QUERY_KEYS.devicePings(deviceId), 'cursor'],
      queryFn: (params) => pingsApi.getByDeviceCursor(deviceId, params),
      enabled: !!deviceId,
    })

  const handleExportCsv = () => {
    const rows = items.map((p) => ({
      Date: formatDate(p.pingAt),
      Status: getStatusLabel(p.status),
      'IP Address': p.ipAddress || '--',
      Geolocation: formatGeolocation(p.geolocation),
    }))
    exportToCsv(`activity-log-${deviceId}.csv`, rows)
  }

  return (
    <div style={{ padding: '24px 16px', height: '100%', display: 'flex', flexDirection: 'column', background: '#f8fafc' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1e293b', margin: '0 0 8px' }}>
            Activity Log
          </h1>
          <p style={{ fontSize: 14, color: '#64748b', margin: 0, maxWidth: 500 }}>
            See a detailed record of the events and actions that happened within the past month.
          </p>
        </div>
        <Button
          icon={<DownloadOutlined />}
          onClick={handleExportCsv}
          disabled={items.length === 0}
          style={{ borderRadius: 8, fontWeight: 500, flexShrink: 0 }}
        >
          Export CSV
        </Button>
      </div>

      <DataTable<Ping>
        columns={columns}
        dataSource={items}
        rowKey="id"
        loading={isLoading}
        showSN={false}
        infiniteScroll={{ isFetchingNextPage, hasNextPage, fetchNextPage }}
        locale={{
          emptyText: (
            <Empty
              image={<HistoryOutlined style={{ fontSize: 48, color: '#cbd5e1' }} />}
              description={
                <span style={{ color: '#94a3b8' }}>No activity recorded yet</span>
              }
            />
          ),
        }}
      />
    </div>
  )
}
